import { WebsiteWithConnections } from '../../types/website';
import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Card, IconButton, Input } from '../ui';
import { InfoField, EmptyValue, WebsiteEditForm } from './WebsiteInfoPanel';

interface DNSInfoPanelProps {
  website: WebsiteWithConnections | null;
  isEditMode: boolean;
  editForm: WebsiteEditForm;
  setEditForm: (val: WebsiteEditForm) => void;
}

export default function DNSInfoPanel({ website, isEditMode, editForm, setEditForm }: DNSInfoPanelProps) {
  const [newNameserver, setNewNameserver] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [decryptedPassword, setDecryptedPassword] = useState('••••••••');

  // Parse the nameservers from JSON
  const nameservers: string[] = Array.isArray(website?.nameservers)
    ? website.nameservers
    : [];

  // Convert nameservers for editing
  const editableNameservers = Array.isArray(editForm.nameservers)
    ? editForm.nameservers
    : [];

  const addNameserver = () => {
    if (!newNameserver.trim()) return;

    const updatedNameservers = [...editableNameservers, newNameserver.trim()];
    setEditForm({ ...editForm, nameservers: updatedNameservers });
    setNewNameserver('');
  };

  // Function to toggle password visibility
  const togglePasswordVisibility = async () => {
    if (!showPassword && website?.registrar_password) {
      if (!String(website.registrar_password).startsWith('\\x')) {
        setDecryptedPassword(website.registrar_password);
      } else {
        try {
          const { data, error } = await supabase.rpc(
            'decrypt_registrar_password',
            { encrypted_password: website.registrar_password }
          );
          if (!error && data) setDecryptedPassword(data);
        } catch (err) {
          console.error('Error decrypting password:', err);
          setDecryptedPassword('Error decrypting');
        }
      }
    }
    setShowPassword(!showPassword);
  };

  const removeNameserver = (index: number) => {
    const updatedNameservers = [...editableNameservers];
    updatedNameservers.splice(index, 1);
    setEditForm({ ...editForm, nameservers: updatedNameservers });
  };

  const eyeButton = (onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className="pressable hover-wash"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '24px',
        height: '24px',
        borderRadius: 'var(--radius-xs)',
        border: 'none',
        background: 'transparent',
        color: 'var(--text-faint)',
        cursor: 'pointer',
      }}
      aria-label={showPassword ? 'Hide Password' : 'Show Password'}
    >
      {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
    </button>
  );

  return (
    <Card eyebrow="DNS">
      {isEditMode ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <Input
            label="Registrar"
            placeholder="GoDaddy, Namecheap, etc."
            value={editForm.registrar || ''}
            onChange={(e) => setEditForm({ ...editForm, registrar: e.target.value })}
          />

          <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--space-4)' }}>
            <Input
              label="Registrar Username"
              value={editForm.registrar_username || ''}
              onChange={(e) => setEditForm({ ...editForm, registrar_username: e.target.value })}
            />

            <div>
              <div className="t-eyebrow" style={{ marginBottom: '6px' }}>Registrar Password</div>
              <div className="flex items-center" style={{ gap: '6px' }}>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={editForm.registrar_password || ''}
                  onChange={(e) => setEditForm({ ...editForm, registrar_password: e.target.value })}
                  style={{ flex: 1 }}
                />
                {eyeButton(() => setShowPassword(!showPassword))}
              </div>
            </div>
          </div>

          <Input
            label="DNS Host"
            placeholder="Cloudflare, Namecheap, etc."
            value={editForm.dns_host}
            onChange={(e) => setEditForm({ ...editForm, dns_host: e.target.value })}
          />

          <div>
            <div className="t-eyebrow" style={{ marginBottom: '6px' }}>Nameservers</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* List existing nameservers with delete buttons */}
              {editableNameservers.map((nameserver: string, index: number) => (
                <div key={index} className="flex items-center" style={{ gap: '8px' }}>
                  <Input
                    value={nameserver}
                    style={{ flex: 1 }}
                    onChange={(e) => {
                      const updatedNameservers = [...editableNameservers];
                      updatedNameservers[index] = e.target.value;
                      setEditForm({ ...editForm, nameservers: updatedNameservers });
                    }}
                  />
                  <IconButton
                    icon="trash-2"
                    size="sm"
                    variant="outline"
                    label="Remove Nameserver"
                    onClick={() => removeNameserver(index)}
                    style={{ color: 'var(--danger-400)' }}
                  />
                </div>
              ))}

              {/* Add new nameserver */}
              <div className="flex items-center" style={{ gap: '8px' }}>
                <Input
                  placeholder="ns1.example.com"
                  value={newNameserver}
                  style={{ flex: 1 }}
                  onChange={(e) => setNewNameserver(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addNameserver();
                    }
                  }}
                />
                <IconButton
                  icon="plus"
                  size="sm"
                  variant="outline"
                  label="Add Nameserver"
                  onClick={addNameserver}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--space-5) var(--space-6)' }}>
          <InfoField label="Registrar">
            {website?.registrar || <EmptyValue />}
          </InfoField>

          <InfoField label="DNS Host">
            {website?.dns_host || <EmptyValue />}
          </InfoField>

          <InfoField label="Registrar Username" mono>
            {website?.registrar_username || <EmptyValue />}
          </InfoField>

          <InfoField label="Registrar Password" mono>
            {website?.registrar_password ? (
              <>
                <span>{showPassword ? decryptedPassword : '••••••••'}</span>
                {eyeButton(togglePasswordVisibility)}
              </>
            ) : (
              <EmptyValue />
            )}
          </InfoField>

          <InfoField label="Nameservers" mono>
            {nameservers && nameservers.length > 0 ? (
              <span style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {nameservers.map((ns: string, index: number) => (
                  <span key={index}>{ns}</span>
                ))}
              </span>
            ) : (
              <EmptyValue />
            )}
          </InfoField>
        </div>
      )}
    </Card>
  );
}
